import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { fetchSignupStatus } from '../api/auth'
import { Footer } from '../components/Footer'
import { LanguageToggle } from '../components/LanguageToggle'
import { ThemeToggle } from '../components/ThemeToggle'
import { useT } from '../i18n/context'
import { useAuthStore } from '../stores/auth'
import { PageLoader } from '../components/ui/Spinner'

/**
 * Öffentliche Startseite (analog pages/index.vue des Nuxt-Frontends):
 * Eingeloggte landen direkt bei der Zeiterfassung, alle anderen auf der
 * Landing. Das Gate entspricht der auth.global.ts-Middleware der Referenz.
 */
export function HomePage() {
    const status = useAuthStore(s => s.status)
    if (status === 'boot') return <PageLoader />
    if (status === 'authenticated') return <Navigate to='/track' replace />
    return <LandingPage />
}

function LandingPage() {
    const t = useT()
    // Signup-Kill-Switch: CTA nur zeigen, solange Registrierung offen ist
    const [signupEnabled, setSignupEnabled] = useState<boolean | null>(null)

    useEffect(() => {
        fetchSignupStatus()
            .then(res => setSignupEnabled(res.enabled))
            .catch(() => setSignupEnabled(null))
    }, [])

    return (
        <div className='flex min-h-screen flex-col bg-gradient-to-b from-indigo-50 to-slate-100 dark:from-slate-900 dark:to-slate-950'>
            <div className='absolute top-4 right-4 flex items-center gap-2'>
                <ThemeToggle className='bg-white/60 dark:bg-slate-800/60' />
                <LanguageToggle />
            </div>

            <main className='mx-auto flex w-full max-w-4xl flex-1 flex-col items-center gap-14 px-4 py-12 sm:py-16'>
                {/* Beta-Hinweis: Der Dienst ist live erreichbar, aber noch
                     nicht für den produktiven Einsatz gedacht. */}
                <div
                    data-testid='landing-beta-banner'
                    role='note'
                    className='w-full max-w-3xl rounded-lg border border-amber-300/60 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-800 dark:text-amber-200'
                >
                    {t('landing.beta_banner')}
                </div>

                {/* Hero */}
                <section
                    data-testid='landing-hero'
                    className='flex flex-col items-center gap-5 px-2 text-center'
                >
                    <div className='flex size-16 items-center justify-center rounded-2xl bg-indigo-600 dark:bg-indigo-500 text-white shadow-sm'>
                        <svg
                            className='h-9 w-9'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2}
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            aria-hidden='true'
                        >
                            <circle cx='12' cy='12' r='8.5' />
                            <path d='M8 12.5l2.8 2.8 5.2-6' />
                        </svg>
                    </div>
                    <h1 className='text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-50'>
                        {t('landing.hero_title')}
                    </h1>
                    <p className='max-w-2xl text-lg text-slate-600 dark:text-slate-300'>
                        {t('landing.hero_subtitle')}
                    </p>
                    <div className='flex flex-wrap items-center justify-center gap-3'>
                        {signupEnabled !== false && (
                            <Link
                                to='/login?mode=register'
                                data-testid='landing-signup'
                                className='inline-flex items-center justify-center rounded-lg bg-indigo-700 dark:bg-indigo-600 px-6 py-3 text-base font-medium text-white shadow-sm transition-colors hover:bg-indigo-800 dark:hover:bg-indigo-500'
                            >
                                {t('landing.cta_sign_up')}
                            </Link>
                        )}
                        <Link
                            to='/login'
                            data-testid='landing-signin'
                            className='inline-flex items-center justify-center rounded-lg px-6 py-3 text-base font-medium text-slate-700 dark:text-slate-200 transition-colors hover:bg-white/70 dark:hover:bg-slate-800/60'
                        >
                            {t('landing.cta_sign_in')}
                        </Link>
                    </div>
                </section>

                {/* Features */}
                <section className='grid w-full grid-cols-1 gap-4 sm:grid-cols-2'>
                    <FeatureCard
                        title={t('landing.feature_timer_title')}
                        text={t('landing.feature_timer_text')}
                        iconPath='M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                    <FeatureCard
                        title={t('landing.feature_reports_title')}
                        text={t('landing.feature_reports_text')}
                        iconPath='M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z'
                    />
                    <FeatureCard
                        title={t('landing.feature_teams_title')}
                        text={t('landing.feature_teams_text')}
                        iconPath='M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z'
                    />
                    <FeatureCard
                        title={t('landing.feature_billing_title')}
                        text={t('landing.feature_billing_text')}
                        iconPath='M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                </section>
            </main>

            <Footer />
        </div>
    )
}

function FeatureCard({
    title,
    text,
    iconPath,
}: {
    title: string
    text: string
    iconPath: string
}) {
    return (
        <div className='rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-md'>
            <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-8 w-8 text-indigo-700 dark:text-indigo-400'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}
                aria-hidden='true'
            >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d={iconPath}
                />
            </svg>
            <h2 className='mt-3 text-lg font-semibold text-slate-800 dark:text-slate-100'>
                {title}
            </h2>
            <p className='mt-1 text-sm text-slate-500 dark:text-slate-400'>
                {text}
            </p>
        </div>
    )
}
